/* ============================================================
 * src/screens/insights/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-insights.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired; all CIRCA reads in this bundle have
// been wired to real Convex subscriptions or honestly deferred. No fixture
// dependency remains here.
import { money } from '../../lib/format';
import { navigate } from '../../lib/nav';
// T333 (FR-012, WIRE-6) — Cost and Uptime wired to public Convex queries.
// The other analytics surfaces (`/quality`, `/player-health`,
// `/ad-performance`, `/analytics`) defer honestly behind a "próximamente"
// state: there is no public rollup query family today and FR-012 /
// NFR-006 forbid speculative wiring.
import { useQuery } from 'convex/react';
import { api } from '@convex/api';

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — Insights arc + simple charts
   ============================================================ */

export function LineChart({ data, h = 220, color = 'var(--ink)', fmt = (v) => v }) {
  const w = 800; const pad = 8;
  const max = Math.max(...data) * 1.1, min = 0;
  const rng = max - min || 1;
  const pts = data.map((d, i) => [pad + (i / (data.length - 1)) * (w - pad * 2), h - 24 - ((d - min) / rng) * (h - 40)]);
  const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: h, display: 'block', overflow: 'visible' }}>
      {[0, 0.5, 1].map(f => <line key={f} x1={0} x2={w} y1={24 + f * (h - 40)} y2={24 + f * (h - 40)} stroke="var(--lightest-gray)" strokeWidth={1} />)}
      <path d={line + ` L${pts[pts.length-1][0]} ${h-24} L${pts[0][0]} ${h-24} Z`} fill={color} opacity={0.06} />
      <path d={line} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function BarChart({ data, labels, h = 200, color = 'var(--deep-green)' }) {
  const max = Math.max(...data) || 1;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: h }}>
      {data.map((d, i) => (
        <div key={i} title={String(d)} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%', justifyContent: 'flex-end' }}>
          <div style={{ width: '100%', height: `${(d / max) * 100}%`, background: color, borderRadius: '4px 4px 0 0', minHeight: 2 }} />
          {labels && <span className="t-mono" style={{ fontSize: 9, color: 'var(--muted-slate)' }}>{labels[i]}</span>}
        </div>
      ))}
    </div>
  );
}
/* prototype: window.LineChart/BarChart globals -> ESM exports (added at function decl). */

function dateRangeSelect() { return <Select value="Últimos 7 días" onChange={() => {}} options={['Hoy', 'Últimos 7 días', 'Últimos 30 días', 'Año actual']} />; }

/**
 * T333 (FR-012, WIRE-6) — `/analytics` wired to `api.analytics.*`.
 *
 * Two honest public subscriptions land the headline tiles and the
 * top-channels list:
 *   - `api.analytics.getWatchTimeSummary` → `{ watchTimeSeconds,
 *     totalSegments, rangeDays }` for the 30-day watch-time headline.
 *   - `api.analytics.getTopChannelsByWatchTime` → top-5 channels by
 *     aggregate watch-time, with channel-name join.
 *
 * Honest deferrals (FR-012 / NFR-006):
 *   - "Horas transmitidas" / "Calidad promedio" / per-hour-of-day /
 *     per-device / "Videos principales" — no public query family;
 *     defer behind a "próximamente" footnote rather than fabricate.
 */
function formatHours(seconds: number): string {
  return Math.round(seconds / 3600).toLocaleString('es-MX') + ' h';
}

export function Analytics() {
  const watch = useQuery(api.analytics.getWatchTimeSummary, { rangeDays: 30 }) as
    | { watchTimeSeconds: number; totalSegments: number; rangeDays: number }
    | undefined;
  const topChannels = useQuery(api.analytics.getTopChannelsByWatchTime, { topN: 5, rangeDays: 30 }) as
    | Array<{ channelId: string; channelName: string | null; watchTimeSeconds: number; totalSegments: number }>
    | undefined;
  const loading = watch === undefined || topChannels === undefined;
  return (
    <div>
      <PageHead title="Analíticas" description="Tiempo de visualización y canales principales (últimos 30 días)." />
      {loading ? <><SkeletonStatRow n={4} /><div style={{ height: 24 }} /><SkeletonPanel h={220} /></> : <>
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Tiempo de visualización" value={watch ? formatHours(watch.watchTimeSeconds) : '—'} hint="últimos 30 d" />
        <StatCard label="Horas transmitidas" value="—" hint="próximamente" />
        <StatCard label="Calidad promedio" value="—" hint="próximamente" />
        <StatCard label="Canales con actividad" value={topChannels ? topChannels.length : '—'} hint="últimos 30 d" />
      </div>
      <Panel title="Canales principales (últimos 30 días)" pad={0} style={{ marginBottom: 24 }}>
        {topChannels && topChannels.length > 0 ? topChannels.map((c, i) => (
          <div key={c.channelId} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 20px', borderBottom: i < topChannels.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
            <span className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)', width: 16 }}>{i + 1}</span>
            <span style={{ flex: 1, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.channelName ?? c.channelId}</span>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{formatHours(c.watchTimeSeconds)}</span>
          </div>
        )) : <div style={{ padding: 16 }}><EmptyState icon="chart" title="Sin actividad registrada" body="Todavía no hay tiempo de visualización agregado para mostrar." /></div>}
      </Panel>
      <Panel>
        <EmptyState
          icon="activity"
          title="Próximamente — Más analíticas"
          body="Audiencia por hora del día, distribución por dispositivo y top de videos llegarán cuando se publiquen las consultas agregadas correspondientes."
        />
      </Panel>
      </>}
    </div>
  );
}

/**
 * T333 (FR-012, WIRE-6) — `/quality` deferred honestly.
 *
 * No public quality-of-experience rollup query exists today: there is no
 * `qoeBeacons` table aggregator, no public player-event roll-up, and the
 * prototype's bloqueos/huecos/caídas vocabulary maps onto internal
 * telemetry the public surface does not expose. Per FR-012 / NFR-006 the
 * surface defers honestly behind a "próximamente" state rather than
 * fabricating a score from random series.
 */
export function ViewerQuality() {
  return (
    <div>
      <PageHead title="Calidad de visión" description="Bloqueos, huecos, caídas y errores en las últimas 24 horas." />
      <Panel>
        <EmptyState
          icon="activity"
          title="Próximamente — Calidad de visión"
          body="La medición agregada de calidad (bloqueos, huecos, caídas) llegará en una próxima entrega. Mientras tanto, revisa la salud por canal en la página de cada canal."
          action={<Btn variant="ghost" iconRight="arrowRight" onClick={() => navigate('/channels')}>Ir a canales</Btn>}
        />
      </Panel>
    </div>
  );
}

/**
 * T333 (FR-012, WIRE-6) — `/cost` wired to `api.costSummary.*`.
 *
 * Three honest data sources, all already public and auth-gated:
 *   - `api.costSummary.getMonthProjection` — month-to-date + projected total
 *     in cents (`{ mtdCents, projectedTotalCents, daysElapsed, daysInMonth }`).
 *   - `api.costSummary.getDailyTrend` — `{ date, totalCostCents }[]` for the
 *     last 30 days, ascending. Feeds the trend line.
 *   - `api.costSummary.getMonthToDateByProvider` — `{ provider,
 *     totalCostCents }[]` for the current month. Powers the per-category
 *     rollup.
 *
 * Honest deferrals (FR-012 / NFR-006):
 *   - "Variación diaria" and the "Reparto del gasto" pie are dropped from
 *     this surface — there is no public yesterday-vs-today delta query
 *     today, and the prototype's hardcoded category split (border/aws/ai)
 *     does not match the real `apiUsageDaily.service` taxonomy.
 *   - "Costo por canal (mes a la fecha)" — no public per-channel cost
 *     rollup exists; defer behind a "próximamente" footnote.
 *
 * `money()` from `lib/format.ts` expects a USD number; cents → USD is a
 * straight divide by 100.
 */
function centsToUsd(cents: number): number {
  return cents / 100;
}

export function Cost() {
  const projection = useQuery(api.costSummary.getMonthProjection, {}) as
    | { mtdCents: number; projectedTotalCents: number; daysElapsed: number; daysInMonth: number }
    | undefined;
  const dailyTrend = useQuery(api.costSummary.getDailyTrend, { rangeDays: 30 }) as
    | Array<{ date: string; totalCostCents: number }>
    | undefined;
  const byProvider = useQuery(api.costSummary.getMonthToDateByProvider, {}) as
    | Array<{ provider: string; totalCostCents: number }>
    | undefined;
  const loading = projection === undefined || dailyTrend === undefined || byProvider === undefined;
  return (
    <div>
      <PageHead title="Costos" description="Gasto del mes en curso, proyección y desglose por categoría." actions={dateRangeSelect()} />
      <FreshnessBanner show message="Los datos de costo provienen de resúmenes diarios, no en tiempo real. Se actualizan cada pocas horas." />
      {loading ? <><SkeletonStatRow /><SkeletonPanel h={200} /><div style={{ height: 24 }} /><SkeletonTable rows={6} cols={4} /></> : <>
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Mes a la fecha" value={projection ? money(centsToUsd(projection.mtdCents)) : '—'} />
        <StatCard label="Proyección fin de mes" value={projection ? money(centsToUsd(projection.projectedTotalCents)) : '—'} />
        <StatCard
          label="Ayer"
          value={dailyTrend && dailyTrend.length > 0 ? money(centsToUsd(dailyTrend[dailyTrend.length - 1].totalCostCents)) : '—'}
        />
        {/* T333 (FR-012): "Variación diaria" requires a yesterday-vs-day-
            before comparator. Honest em-dash until a public delta query
            lands — never fabricate a percentage. */}
        <StatCard label="Variación diaria" value="—" hint="próximamente" />
      </div>
      <Panel title="Tendencia diaria (USD)" style={{ marginBottom: 24 }}>
        {dailyTrend && dailyTrend.length > 0
          ? <LineChart data={dailyTrend.map((d) => centsToUsd(d.totalCostCents))} color="var(--deep-green)" />
          : <EmptyState icon="chart" title="Sin datos de costo todavía" body="Aún no hay resúmenes diarios para el rango seleccionado." />}
      </Panel>
      <Panel title="Por categoría" pad={0}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 100px', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 12, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
          <span>Categoría</span><span>Mes a la fecha</span><span>Tend.</span>
        </div>
        {byProvider && byProvider.length > 0 ? byProvider.map((p, i) => (
          <div key={p.provider} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 100px', padding: '14px 20px', alignItems: 'center', borderBottom: i < byProvider.length - 1 ? '1px solid var(--lightest-gray)' : 'none', fontSize: 14 }}>
            <span>{p.provider}</span>
            <span>{money(centsToUsd(p.totalCostCents))}</span>
            {/* Per-provider sparkline requires a daily-by-provider time series
                that is not in the public surface today; honest placeholder. */}
            <span style={{ color: 'var(--muted-slate)', fontSize: 12 }}>—</span>
          </div>
        )) : <div style={{ padding: 16 }}><EmptyState icon="chart" title="Sin gasto registrado" body="Todavía no se han imputado costos a ningún proveedor este mes." /></div>}
      </Panel>
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Btn size="sm" variant="text" iconRight="arrowRight" onClick={() => navigate('/provider-spend')}>Ver gasto por proveedor</Btn>
      </div>
      </>}
    </div>
  );
}

/**
 * T333 (FR-012, WIRE-6) — `/uptime` wired to `api.sloDaily.*`.
 *
 * Two honest public subscriptions:
 *   - `api.sloDaily.getWorkspace90DayUptime` → `{ date, uptimePercent }[]`
 *     for the 90-day grid and the "Disponibilidad (90 d)" headline.
 *   - `api.sloDaily.getWorkspaceIncidents` → raw `sloDaily` rows with
 *     `downtimeMinutes > 0`, newest-first (default cap 50). Powers the
 *     incidents panel.
 *
 * Honest deferrals (FR-012 / NFR-006):
 *   - "Presupuesto de error" requires a per-month error-budget calculation
 *     that the public surface does not expose today; honest em-dash.
 *   - Per-channel 30-day uptime would require fan-out by channel id; the
 *     workspace aggregator is a single batch (NFR-002). The per-channel
 *     view is the existing channel-detail surface (`/channels/:id`),
 *     reached via the "Detalle por canal" link.
 *
 * The 99.9% SLO target ribbon stays — it is a configuration constant, not
 * a fabricated measurement.
 */
const SLO_TARGET_PERCENT = 99.9;

export function Uptime() {
  const daily = useQuery(api.sloDaily.getWorkspace90DayUptime, {}) as
    | Array<{ date: string; uptimePercent: number }>
    | undefined;
  const incidents = useQuery(api.sloDaily.getWorkspaceIncidents, {}) as
    | Array<{ _id: string; channelId?: string; date: string; hour: number; downtimeMinutes: number; uptimeMinutes: number }>
    | undefined;
  const loading = daily === undefined || incidents === undefined;

  // Workspace-wide 90-day average uptime (% time-weighted by day).
  const avg90d = !daily || daily.length === 0
    ? null
    : daily.reduce((s, d) => s + d.uptimePercent, 0) / daily.length;
  const meetsSlo = avg90d !== null && avg90d >= SLO_TARGET_PERCENT;

  return (
    <div>
      <PageHead title="Disponibilidad" description="Cuadrícula de 90 días y incidentes recientes en todo el espacio de trabajo." />
      {loading ? <><SkeletonStatRow n={4} /><div style={{ height: 24 }} /><SkeletonPanel h={240} /></> : <>
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard
          label="Disponibilidad (90 d)"
          value={avg90d !== null ? `${avg90d.toFixed(2)}%` : '—'}
          tone={meetsSlo ? 'good' : avg90d !== null ? 'warn' : undefined}
          icon="gauge"
        />
        <StatCard label="Objetivo (SLO)" value={`${SLO_TARGET_PERCENT}%`} icon="checkCircle" help="Objetivo de nivel de servicio. La disponibilidad por encima de este umbral cumple el compromiso." />
        {/* T333 (FR-012): the public surface has no error-budget query;
            defer honestly until one lands. */}
        <StatCard label="Presupuesto de error" value="—" hint="próximamente" icon="activity" />
        <StatCard
          label="Incidentes (90 d)"
          value={incidents ? incidents.length : '—'}
          tone={incidents && incidents.length === 0 ? 'good' : incidents && incidents.length > 0 ? 'warn' : undefined}
          icon="alertCircle"
        />
      </div>
      <Panel title="Cuadrícula de 90 días" style={{ marginBottom: 24 }}>
        {daily && daily.length > 0 ? (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 16 }}>
              {daily.map((d, i) => {
                const col = d.uptimePercent >= 99.9 ? 'var(--good)' : d.uptimePercent >= 99 ? 'var(--warn)' : 'var(--bad)';
                return <div key={d.date} title={`${d.date}: ${d.uptimePercent.toFixed(2)}%`} style={{ width: 18, height: 18, borderRadius: 3, background: col, opacity: 0.85, outline: i === daily.length - 1 ? '2px solid var(--cta)' : 'none', outlineOffset: 1 }} />;
              })}
            </div>
            <div style={{ display: 'flex', gap: 18, fontSize: 12, color: 'var(--muted-slate)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--good)' }} />≥99.9%</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--warn)' }} />99–99.9%</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--bad)' }} />&lt;99%</span>
            </div>
          </>
        ) : (
          <EmptyState icon="gauge" title="Sin datos de disponibilidad" body="Aún no hay rollups SLO para mostrar." />
        )}
      </Panel>
      <Panel title="Incidentes (90 días)">
        {incidents && incidents.length === 0
          ? <EmptyState icon="checkCircle" title="Sin incidentes registrados" body="100% de disponibilidad en todo el espacio de trabajo durante el periodo." />
          : (
            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr', padding: '12px 0 8px', borderBottom: '1px solid var(--border-cool)', fontSize: 11, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
              <span>Fecha</span><span>Hora</span><span>Minutos caídos</span><span>Minutos activos</span>
            </div>
          )}
        {incidents && incidents.length > 0 && incidents.map((inc, i) => (
          <div key={inc._id} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr', padding: '13px 0', alignItems: 'center', borderBottom: i < incidents.length - 1 ? '1px solid var(--lightest-gray)' : 'none', fontSize: 14 }}>
            <span className="t-mono">{inc.date}</span>
            <span className="t-mono">{String(inc.hour).padStart(2, '0')}:00</span>
            <span className="t-mono" style={{ color: 'var(--bad)' }}>{inc.downtimeMinutes}</span>
            <span className="t-mono" style={{ color: 'var(--muted-slate)' }}>{inc.uptimeMinutes}</span>
          </div>
        ))}
      </Panel>
      </>}
    </div>
  );
}

/**
 * T333 (FR-012, WIRE-6) — `/player-health` deferred honestly.
 *
 * No public player-telemetry aggregator exists today (the
 * `qoeBeaconAggregator` cron writes internal-only state). Per FR-012 /
 * NFR-006 this surface defers honestly until a public player-health
 * query family lands.
 */
export function PlayerHealth() {
  return (
    <div>
      <PageHead title="Salud del reproductor" description="Cómo se comporta el reproductor en los navegadores de tus espectadores." />
      <Panel>
        <EmptyState
          icon="activity"
          title="Próximamente — Salud del reproductor"
          body="Los indicadores agregados de reproductor (sesiones activas, tiempo de inicio, ratio de búfer) llegarán cuando se publique la consulta agregada de telemetría."
        />
      </Panel>
    </div>
  );
}

/**
 * T333 (FR-012, WIRE-6) — `/ad-performance` deferred honestly.
 *
 * The ad-break outcome rollup lives in internal storage today; there is
 * no public `adBreakLog` aggregator query family. Per FR-012 / NFR-006
 * defer honestly rather than fabricating an outcomes split from a mock
 * fixture.
 */
export function AdPerformance() {
  return (
    <div>
      <PageHead title="Entrega de anuncios" description="Resultados de cortes de anuncio en todo el espacio de trabajo." />
      <Panel>
        <EmptyState
          icon="zap"
          title="Próximamente — Entrega de anuncios"
          body="El desglose agregado de cortes de anuncio (disparados, inserciones observadas, fallidos) llegará cuando se publique la consulta agregada del log de cortes."
          action={<Btn variant="ghost" iconRight="arrowRight" onClick={() => navigate('/system-health')}>Revisar salud del sistema</Btn>}
        />
      </Panel>
    </div>
  );
}

